import React from 'react';
import { Card, Row, Col, Statistic, Tag, Button, Tooltip, Spin, Empty } from 'antd';
import { ReloadOutlined, SettingOutlined } from '@ant-design/icons';
import { useTranslationWithNamespace } from '../../hooks/useTranslationWithNamespace';
import { useIndexerStatus, useDepositDetectionMetrics } from '../../hooks/useIndexerManagement';
import { useNavigate } from 'react-router-dom';
import { IndexerStatus } from '../../types/indexer';

const DepositDetectionStatusPanel: React.FC = () => {
  const { t: indexerT } = useTranslationWithNamespace('indexer');
  const { t: commonT } = useTranslationWithNamespace('common');
  const navigate = useNavigate();

  const { data: indexerStatus, isLoading: isStatusLoading, refetch: refetchStatus } = useIndexerStatus();
  const { data: metrics, isLoading: isMetricsLoading } = useDepositDetectionMetrics();

  const isLoading = isStatusLoading || isMetricsLoading;

  // Count indexers by status
  const countByStatus = indexerStatus?.data ? {
    running: indexerStatus.data.filter((i: IndexerStatus) => i.status === 'running').length,
    stopped: indexerStatus.data.filter((i: IndexerStatus) => i.status === 'stopped').length,
    error: indexerStatus.data.filter((i: IndexerStatus) => i.status === 'error').length
  } : { running: 0, stopped: 0, error: 0 };

  // Get indexers with errors
  const indexersWithErrors = indexerStatus?.data ?
    indexerStatus.data.filter((i: IndexerStatus) => i.status === 'error') : [];

  return (
    <Card
      title={indexerT('depositDetection.statusTitle', 'Deposit Detection Status')}
      extra={
        <Button
          icon={<ReloadOutlined />}
          onClick={() => refetchStatus()}
          loading={isLoading}
          size="small"
        >
          {commonT('actions.refresh')}
        </Button>
      }
    >
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
        </div>
      ) : !indexerStatus?.data ? (
        <Empty description={indexerT('depositDetection.noData', 'No indexer data available')} />
      ) : (
        <>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <Statistic
                title={indexerT('depositDetection.runningIndexers', 'Running Indexers')}
                value={countByStatus.running}
                valueStyle={{ color: '#3f8600' }}
                suffix={`/ ${indexerStatus.data.length}`}
              />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title={indexerT('depositDetection.depositsLast24h', 'Deposits (24h)')}
                value={metrics?.depositsLast24h || 0}
              />
            </Col>
            <Col xs={24} sm={8}>
              <Statistic
                title={indexerT('depositDetection.amountLast24h', 'Amount (24h)')}
                value={metrics?.amountLast24h || '0.00'}
                precision={2}
              />
            </Col>
          </Row>

          {indexersWithErrors.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <h4>{indexerT('depositDetection.indexersWithErrors', 'Indexers with Errors:')}</h4>
              <div>
                {indexersWithErrors.map((indexer: IndexerStatus) => (
                  <Tooltip
                    key={indexer.blockchain}
                    title={indexer.error || indexerT('depositDetection.unknownError', 'Unknown error')}
                  >
                    <Tag
                      color="red"
                      style={{ margin: '4px' }}
                    >
                      {(indexer.blockchain || 'unknown').toUpperCase()}
                    </Tag>
                  </Tooltip>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Button
              type="primary"
              icon={<SettingOutlined />}
              onClick={() => navigate('/indexers')}
            >
              {indexerT('depositDetection.manage', 'Manage Indexers')}
            </Button>
          </div>
        </>
      )}
    </Card>
  );
};

export default DepositDetectionStatusPanel;
